import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  assignQuestionsFromTemplate,
  deleteTemplate,
  getAllTemplates,
  getTemplates,
  toggleTemplate,
  updateFootnote,
  updateFootnoteFul,
  updateQuestions,
  updateSections,
  updateTemplateName,
  updateTemplateProblemQuestions,
} from "../../api/reportsApi";
import { lockToast, successToast } from "../../helpers/successToast";
import {
  EditableReportQuestion,
  ProblemAnswer,
  TemplateEditableSection,
  TemplateCategory,
} from "../../types/Rows";
import { useTemplateContext } from "../../pages/template/templateContext";
import AddItemModal from "../../pages/template/addItemModal";
import { useCallback, useEffect, useState } from "react";
import { FaLockOpen } from "react-icons/fa";
import { FaLock } from "react-icons/fa";
import { toast } from "react-toastify";
import { useUserContext } from "../../pages/user/authProvider";
import AddItemWithExistingPicker, {
  InputType,
} from "../../pages/template/AddItemWithExistingPicker";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useAddTemplateSection } from "../../hooks/useAddTemplateSection";

const TemplatesToolbar = ({
  name,
  templateId,
  isLocked,
  author,
  footer,
  footerFul,
  templateType,
  studyId,
  studyName,
}: {
  name: string;
  templateId: string;
  isLocked: boolean;
  author: string;
  footer: string;
  footerFul: string;
  templateType?: TemplateCategory;
  studyId?: string;
  studyName?: string;
}) => {
  const isCentralMonitoring = templateType === TemplateCategory.CENTRAL_MONITORING;

  const queryClient = useQueryClient();
  const { questionsToSave, sectionsToSave, markSaved } = useTemplateContext();
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState<string>(name ?? "");
  const { email } = useUserContext();
  const navigate = useNavigate();

  const addSections = useAddTemplateSection(templateId!);

  // Zapis wszystkich zmian naraz; komunikat dopiero po odpowiedzi serwera
  const saveAll = async () => {
    const questions: EditableReportQuestion[] = questionsToSave();
    const sections: TemplateEditableSection[] = sectionsToSave();
    const steps: [string, () => Promise<unknown>][] = [
      ["stopka", () => updateFootnote(templateId, footer)],
      ["stopka FUL", () => updateFootnoteFul(templateId, footerFul)],
    ];
    if (templateName !== name) steps.push(["nazwa", () => updateTemplateName(templateId, templateName)]);
    if (questions.length) steps.push(["pytania", () => updateQuestions(templateId!, questions)]);
    if (sections.length) steps.push(["sekcje", () => updateSections(templateId!, sections)]);

    setSaving(true);
    try {
      const results = await Promise.allSettled(steps.map(([, run]) => run()));
      const failed = steps.filter((_, i) => results[i].status === "rejected").map(([label]) => label);
      if (failed.length === 0) {
        markSaved();
        successToast();
      } else {
        toast.error(`Nie udało się zapisać: ${failed.join(", ")}. Pozostałe zmiany zapisano; spróbuj ponownie.`);
      }
      queryClient.invalidateQueries(["Templates", templateId]);
    } finally {
      setSaving(false);
    }
  };
  const { mutate: toggleLock } = useMutation(() => toggleTemplate(templateId), {
    onSuccess: () => {
      successToast();
      queryClient.invalidateQueries(["Templates", templateId]);
    },
  });

  const { mutate: copyFromExistingTemplate } = useMutation(
    ({ templateId, sourceId }: { templateId: string; sourceId: string }) =>
      assignQuestionsFromTemplate(templateId, sourceId),
    {
      onSuccess: () => {
        successToast();
        queryClient.invalidateQueries(["Templates", templateId]);
      },
    }
  );

  const { mutate: saveProblemQuestions } = useMutation(
    async ({
      templateId,
      problemAnswers,
    }: {
      templateId: string;
      problemAnswers: ProblemAnswer[];
    }) => {
      return await updateTemplateProblemQuestions(templateId, problemAnswers);
    },
    {
      // onSuccess: () => {
      //   queryClient.invalidateQueries(["Templates"]);
      // },
    }
  );

  //const studyTemplates = [];
  //TODO fix
  const { data: studyTemplates } = useQuery(
    ["Templates"],
    async () => getAllTemplates(),
    {
      enabled: templateId != null,
    }
  );

  const { mutate: deleteTemplateAction } = useMutation(
    ({ templateId }: { templateId: string }) => deleteTemplate(templateId),
    {
      onSuccess: () => {
        navigate("", { replace: true });
      },
    }
  );

  const checkAuthor = useCallback(() => {
    console.log("author", author, "Email", email);
    if (author != email) {
      toast.warning("Tylko autor moze zmienić status");
      return;
    }
    toggleLock();
  }, [author, email, toggleLock]);

  const Locked = () => {
    if (isLocked) {
      return (
        <>
          {"Zablokowany"}{" "}
          <FaLock style={{ display: "inline" }} className="m-1" />
        </>
      );
    } else
      return (
        <>
          {"Odblokowany"}{" "}
          <FaLockOpen style={{ display: "inline" }} className="m-1" />
        </>
      );
  };

  return (
    <div className="bg-[var(--sc-surface)] border-b border-[var(--sc-border)] px-6 h-16 flex items-center gap-4 sticky top-0 z-10">
      {/* Back to study */}
      {studyId && (
        <button
          onClick={() => navigate(`/study/${studyId}`)}
          className="flex items-center gap-1.5 text-[12px] text-[var(--sc-muted)] hover:text-[var(--sc-text)] transition-colors bg-transparent border-none cursor-pointer p-0 whitespace-nowrap flex-shrink-0"
        >
          <span className="text-[14px] leading-none">‹</span>
          <span className="max-w-[140px] truncate">{studyName ?? "Badanie"}</span>
        </button>
      )}
      {studyId && <div className="w-px h-5 bg-[var(--sc-border)] flex-shrink-0" />}

      {/* Template name */}
      <div className="flex items-baseline gap-3 flex-1 min-w-0">
        <span className="text-[11px] font-medium tracking-[0.06em] uppercase text-[var(--sc-muted)] whitespace-nowrap">Szablon</span>
        <input
          type="text"
          className="text-[18px] font-semibold text-[var(--sc-text)] border-none bg-transparent outline-none border-b-2 border-transparent focus:border-[var(--sc-amber)] pb-[2px] transition-colors min-w-0 w-64"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Nazwa szablonu"
        />
      </div>

      {/* Author */}
      <div className="flex items-center gap-[6px] text-[11px] text-[var(--sc-muted)] whitespace-nowrap">
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#99AECB] to-[#6B8AAD] flex items-center justify-center text-white text-[9px] font-semibold flex-shrink-0">
          {author?.split("@")[0]?.slice(0, 2).toUpperCase() || "?"}
        </div>
        {author}
      </div>

      {/* Lock toggle */}
      <button
        onClick={checkAuthor}
        className="flex items-center gap-[6px] px-3 py-[6px] rounded-[20px] border border-[var(--sc-border)] bg-[var(--sc-bg)] text-[11px] font-medium whitespace-nowrap transition-colors hover:bg-[var(--sc-border)]"
        style={{ color: isLocked ? "#C07810" : "#1A8055" }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: isLocked ? "#F4A832" : "#2BAE72" }} />
        {isLocked ? "Zablokowany" : "Odblokowany"}
      </button>

      <div className="w-px h-6 bg-[var(--sc-border)]" />

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <AddItemWithExistingPicker
          saveExisting={(sourceId) => {
            if (isLocked) { lockToast(); return; }
            copyFromExistingTemplate({ templateId: templateId!, sourceId });
          }}
          inputType={InputType.PICKFROMEXISTING}
          saveNew={() => {}}
          items={async () =>
            studyTemplates
              ?.map((x) => ({ id: x.id, name: x.studyName + " – " + x.name }))
              .sort((a, b) => a.name.localeCompare(b.name))
              .filter((x) => x.id !== templateId)
          }
          text="Skopiuj z istniejącego"
        >
          ⎘ Skopiuj z istniejącego
        </AddItemWithExistingPicker>

        <AddItemModal
          text="Dodaj sekcję"
          saveItem={(name) => {
            if (isLocked) { lockToast(); return; }
            addSections(name);
          }}
        >
          + Dodaj sekcję
        </AddItemModal>

        <Button
          variant="secondary"
          disabled={isCentralMonitoring}
          className="border-[#F0D0D0] text-[#C03030] hover:bg-[#FEE8E8] hover:border-[#E05050]"
          onClick={async () => {
            if (isCentralMonitoring) { toast.warning("Nie można usunąć szablonu centralnego monitoringu"); return; }
            if (isLocked) { lockToast(); return; }
            if (window.confirm("Czy na pewno chcesz usunąć? Spowoduje to równiez usunięcie powiązanych raportów")) {
              await deleteTemplateAction({ templateId: templateId! });
            }
          }}
          title={isCentralMonitoring ? "Szablony centralnego monitoringu nie mogą być usunięte" : ""}
        >
          {isCentralMonitoring ? "Nie można usunąć" : "Usuń szablon"}
        </Button>

        <button
          className="px-5 py-2 rounded-[7px] text-[13px] font-semibold bg-[#0F0F0F] text-white border-none cursor-pointer hover:bg-[#2A2A2A] dark:bg-[#F0EDE8] dark:text-[#1A1816] dark:hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-wait"
          disabled={saving}
          onClick={() => {
            if (isLocked) { lockToast(); return; }
            saveAll();
          }}
        >
          {saving ? "Zapisywanie…" : "Zapisz zmiany"}
        </button>
      </div>
    </div>
  );
};

export default TemplatesToolbar;
